import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCellarStore } from '../store/CellarStoreContext';
import { expiringWines } from '../domain/expiring';
import {
  NO_FILTERS,
  filterOptions,
  filterWines,
  hasActiveFilters,
  regionChoices,
} from '../domain/filters';
import { FilterBar } from '../components/FilterBar';
import { filtersFromParams, filtersToParams } from '../components/filter-params';
import { StockCard } from '../components/StockCard';
import { t, tf } from '../i18n';
import './ExpiringPage.css';
import './HomePage.css';
import './InventoryPage.css';

/**
 * /c/:cellarId/echeances, "À boire" ("Drink soon" since 2026-10-15; owner requests, 2026-10-03, renamed from
 * "Échéances" 2026-10-13): the wines in stock past their window, then those due within 24 months,
 * each soonest first, on the same compact cards as "Stock", each opening the wine's page. Owner,
 * 2026-10-15: the same search and filters as "Stock", folded behind "Rechercher" and kept in the
 * address; they narrow both groups.
 */
export function ExpiringPage() {
  const { cellarId } = useParams<{ cellarId: string }>();
  const { cellar } = useCellarStore();
  const [params, setParams] = useSearchParams();
  const [filters, setFiltersState] = useState(() => filtersFromParams(params));
  const [searching, setSearching] = useState(false);
  const { past, soon } = useMemo(() => expiringWines(cellar?.wines ?? []), [cellar]);
  const listed = useMemo(() => [...past, ...soon], [past, soon]);
  const options = useMemo(() => filterOptions(listed), [listed]);
  const shownPast = useMemo(() => filterWines(past, filters), [past, filters]);
  const shownSoon = useMemo(() => filterWines(soon, filters), [soon, filters]);

  const setFilters = (next: typeof filters) => {
    setFiltersState(next);
    setParams(filtersToParams(next), { replace: true });
  };

  if (!cellar) {
    return (
      <main>
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  const group = (id: string, title: string, wines: typeof past) =>
    wines.length > 0 && (
      <section aria-labelledby={id} className="expiring__group">
        <h2 id={id}>{title}</h2>
        <div className="card-grid">
          {wines.map((wine) => (
            <StockCard key={wine.id} cellarId={cellarId!} wine={wine} />
          ))}
        </div>
      </section>
    );
  const count = shownPast.length + shownSoon.length;

  return (
    <main className="expiring">
      <header className="inventory-header">
        <div className="inventory-title">
          <h1>{t('nav.expiring')}</h1>
        </div>
        {listed.length > 0 && (
          <div className="inventory-actions">
            <button
              type="button"
              aria-expanded={searching}
              aria-controls="expiring-search-pane"
              onClick={() => setSearching(!searching)}
            >
              {t('inventory.showSearch')}
            </button>
          </div>
        )}
      </header>
      {listed.length === 0 ? (
        <p>{t('expiring.none')}</p>
      ) : (
        <>
          {searching && (
            <div id="expiring-search-pane">
              <FilterBar
                filters={filters}
                options={options}
                regionsFor={(pays) => regionChoices(listed, pays)}
                onChange={setFilters}
                showIncludeEmpty={false}
              />
            </div>
          )}
          {hasActiveFilters(filters) && (
            <p className="inventory-count" aria-live="polite">
              {tf(count === 1 ? 'filters.count.one' : 'filters.count.other', {
                count: String(count),
              })}
              <button
                type="button"
                className="inventory-count__reset"
                onClick={() => setFilters(NO_FILTERS)}
              >
                {t('filters.reset')}
              </button>
            </p>
          )}
          {count === 0 ? (
            <p>{t('filters.empty')}</p>
          ) : (
            <>
              {group('expiring-past', t('expiring.past'), shownPast)}
              {group('expiring-soon', t('expiring.within'), shownSoon)}
            </>
          )}
        </>
      )}
    </main>
  );
}
